import type { ReactNode, Ref } from 'react';
import {
  faArrowUp,
  faCopy,
  faGripVertical,
  faPenToSquare,
  faPlus,
  faTrashCan,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Button } from '@/components/ui/button';
import { ButtonGroup, ButtonGroupSeparator } from '@/components/ui/button-group';
import { Icon } from '@/components/ui/icon';
import { isCollectionBlock } from '@/lib/blocks/collection/blocks';
import { cn } from '@/lib/utils';
import { BLOCK_TYPE, type BlockType, type PageBlock } from '@/types/block';

const BLOCK_TYPE_LABELS = new Map<BlockType, string>([
  ['text', 'Text'],
  ['image', 'Image'],
  ['video', 'Video'],
  ['button', 'Button'],
  ['embed', 'Embed'],
  ['raw_html', 'HTML'],
  ['form', 'Form'],
  ['group', 'Group'],
  ['collection', 'Collection'],
  ['blog_widget', 'Blog Widget'],
  ['case_result_widget', 'Case Result Widget'],
  ['review_widget', 'Review Widget'],
  [BLOCK_TYPE.REVIEW_INDEX, 'Reviews'],
]);

export function getBlockTypeLabel(blockType: BlockType): string {
  return BLOCK_TYPE_LABELS.get(blockType) ?? blockType;
}

export function getBlockPrimaryEditLabel(blockType: BlockType): string {
  if (blockType === 'form') return 'Select form';
  const noun = getBlockTypeLabel(blockType).toLowerCase();
  return `Edit ${noun}`;
}

function getBlockLabel(block: PageBlock): string {
  if (isCollectionBlock(block)) return 'Collection';
  return getBlockTypeLabel(block.blockType);
}

function getMoveLabel(block: PageBlock): string {
  const label = getBlockLabel(block);
  return `Move ${label}`;
}

export interface DragHandleProps {
  setActivatorNodeRef: (el: HTMLElement | null) => void;
  attributes: Record<string, unknown>;
  listeners?: Record<string, unknown>;
}

/** Build DragHandleProps from useSortable return value (centralizes type cast). */
export function createDragHandleProps(
  setActivatorNodeRef: DragHandleProps['setActivatorNodeRef'],
  attributes: unknown,
  listeners?: unknown,
): DragHandleProps {
  return {
    setActivatorNodeRef,
    attributes: attributes as Record<string, unknown>,
    listeners: listeners as Record<string, unknown> | undefined,
  };
}

interface BlockActionToolbarProps {
  block: PageBlock;
  disabled?: boolean;
  dragHandle?: DragHandleProps;
  onSelectParent?: () => void;
  onAddCard?: () => void;
  onOpenCollection?: ReactNode;
  onOpenSettings?: ReactNode;
  onPrimaryEdit?: () => void;
  primaryEditRef?: Ref<HTMLButtonElement>;
  onDuplicate?: () => void;
  onDelete?: () => void;
}

export function BlockActionToolbar({
  block,
  disabled = false,
  dragHandle,
  onSelectParent,
  onAddCard,
  onOpenCollection,
  onOpenSettings,
  onPrimaryEdit,
  primaryEditRef,
  onDuplicate,
  onDelete,
}: BlockActionToolbarProps) {
  const showSelectParent = Boolean(onSelectParent);
  const showAddCard = Boolean(onAddCard);
  const showCollection = Boolean(onOpenCollection);
  const showSettings = Boolean(onOpenSettings);
  const showPrimaryEdit = Boolean(onPrimaryEdit);
  const showDuplicate = Boolean(onDuplicate);
  const showDelete = Boolean(onDelete);
  const hasActions = Boolean(
    showSelectParent ||
      showAddCard ||
      showCollection ||
      showSettings ||
      showPrimaryEdit ||
      showDuplicate ||
      showDelete ||
      dragHandle,
  );

  if (!hasActions) return null;

  const moveLabel = getMoveLabel(block);

  return (
    <div className="flex pt-1 transition-opacity duration-150" data-block-action-toolbar>
      <ButtonGroup
        role="toolbar"
        aria-label="Block actions"
        className={cn(
          'rounded-md border border-primary/20 shadow-lg',
          'cursor-default font-medium overflow-hidden bg-background',
        )}
      >
        {showSelectParent ? (
          <>
            <Button variant="ghost" size="sm" onClick={onSelectParent} disabled={disabled}>
              <Icon icon={faArrowUp} />
              Select Group
            </Button>
            <ButtonGroupSeparator />
          </>
        ) : null}
        {showPrimaryEdit ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              ref={primaryEditRef}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={onPrimaryEdit}
              disabled={disabled}
            >
              <Icon icon={faPenToSquare} />
              {getBlockPrimaryEditLabel(block.blockType)}
            </Button>
            <ButtonGroupSeparator />
          </>
        ) : null}
        {dragHandle ? (
          <Button
            variant="ghost"
            size="sm"
            className="cursor-grab active:cursor-grabbing shrink-0"
            ref={dragHandle.setActivatorNodeRef}
            disabled={disabled}
            {...dragHandle.attributes}
            {...(dragHandle.listeners ?? {})}
            title={moveLabel}
          >
            <Icon icon={faGripVertical} />
            <span>{moveLabel}</span>
          </Button>
        ) : null}
        {showCollection ? (
          <>
            <ButtonGroupSeparator />
            {onOpenCollection}
          </>
        ) : null}
        {showAddCard ? (
          <>
            <ButtonGroupSeparator />
            <Button variant="ghost" size="sm" onClick={onAddCard} disabled={disabled}>
              <Icon icon={faPlus} />
              Add card
            </Button>
          </>
        ) : null}
        {showSettings ? (
          <>
            <ButtonGroupSeparator />
            {onOpenSettings}
          </>
        ) : null}
        {showDuplicate ? (
          <>
            <ButtonGroupSeparator />
            <Button variant="ghost" size="sm" onClick={onDuplicate} disabled={disabled}>
              <Icon icon={faCopy} />
              Duplicate
            </Button>
          </>
        ) : null}
        {showDelete &&
        (showSelectParent ||
          showAddCard ||
          showPrimaryEdit ||
          showCollection ||
          showSettings ||
          showDuplicate ||
          dragHandle) ? (
          <ButtonGroupSeparator />
        ) : null}
        {showDelete ? (
          <Button
            variant="ghost"
            size="sm"
            colorScheme="destructive"
            onClick={onDelete}
            disabled={disabled}
          >
            <Icon icon={faTrashCan} />
            Delete
          </Button>
        ) : null}
      </ButtonGroup>
    </div>
  );
}
